import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useDispatch, useSelector } from 'react-redux';
import LocalizedStrings from 'react-localization';
import LOCALIZATIONS from './localizations';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import './style.css';
import { MODAL_TOGGLE } from '../../redux/actions/app';
import { EXTRA_DATA, ID } from './config';
import { NODE_MODIFY } from '../../redux/actions/board';
import { DEFAULT_NODE } from '../Node/config';
import AdditionalInformation from './AdditionalInformation';

const ModifyNode = () => {

    const dispatch = useDispatch(),
    labels = useMemo( () => new LocalizedStrings( LOCALIZATIONS ), []),
    { locale, modal } = useSelector( state => state.app ),
    [ data, setData ] = useState({ ...DEFAULT_NODE }),
    [ anchorEl, setAnchorEl ] = useState( null ),
    [ options, setOptions ] = useState([]),
    currentNode = useSelector( state => state.board.currentNode ),
    open = Boolean( modal === ID ),
    optionsOpen = Boolean( anchorEl );

    /**
     * @name addData
     * @function
     * @description Adds data to a person
     * @param {string} type 
     */
    const addData = type => () => {
        setData({ ...data, details: { ...data.details, [ type ]: '' } });
        closeMenu();
    }

    /**
     * @name cancel
     * @function
     * @description Hides the modal
     */
    const cancel = () => {
        dispatch({ type: MODAL_TOGGLE });
        setData({ ...DEFAULT_NODE });
        setOptions([]);
    }

    /**
     * @name closeMenu
     * @function
     * @description Closes the add information button menu
     */
    const closeMenu = () => {
        setAnchorEl( null );
    }

    /**
     * @name commit
     * @function
     * @description Commits changes to the context node
     */
    const commit = () => {
        const tempData = { ...data };

        // clean up unused data
        for( let key in tempData.details ){

            const datum = tempData.details[ key ];
            if( 
                ( typeof datum === 'string' && datum.trim().length === 0 ) ||
                ( typeof datum === 'object' && datum.length === 0 ) 
            ){
                delete tempData.details[ key ];
            }
            
        }

        dispatch({ type: NODE_MODIFY, payload: { ...tempData } });
        cancel();
    }

    /**
     * @name onChange
     * @function
     * @description
     * @param {string} name 
     * @param {string} value 
     */
    const onChange = e => {
        setData({ ...data, [ e.target.name ]: e.target.value });
    }

    /**
     * @name onDetailsChange
     * @function
     * @description
     * @param {string} name 
     * @param {string} value 
     */
    const onDetailsChange = ({ target }) => {
        const reg = new RegExp( target.pattern || '', 'g' );
        if( reg.test( target.value ) ){
            setData({ ...data, details: { ...data.details, [ target.name ]: target.value } });
        }
    }

    /**
     * @name openMenu
     * @function
     * @description Opens the "Add Information" menu
     * @param {EventListenerObject} e 
     */
    const openMenu = e => {
        setAnchorEl( e.currentTarget );
    }

    // update locale
    useEffect(() => {
        if( locale !== labels.getLanguage() ) labels.setLanguage( locale )
    }, [ labels, locale ]);

    // update options
    useEffect(() => {
        if( data.id ){
            const tempOptions = Object.keys( EXTRA_DATA[ data.type ] ).filter( key => ( !data.details.hasOwnProperty( key ) ) );
            setOptions([ ...tempOptions ]);
        }
    }, [ data, setOptions ]);

    // set initial data
    useEffect(() => {
        if( currentNode.id ) setData({ ...currentNode });
    }, [ currentNode, setData ]);

    return (
        <>
            <Dialog open={ open } onClose={ cancel }>
                <DialogTitle>
                    { 
                        labels.formatString(
                            labels.title,
                            currentNode?.name || ''
                        )
                    }
                </DialogTitle>
                <DialogContent>
                    <Stack>
                        <FormControl fullWidth>
                            <TextField
                                label={ labels.name }
                                name="name"
                                onChange={ onChange } 
                                value={ data.name || '' } />
                        </FormControl>
                        <Alert severity="info">{ labels.note }</Alert>
                        {
                            Object.keys( data.details ).map(
                                ( key, index ) => (
                                    <AdditionalInformation 
                                        key={ 'additional-info-' + index } 
                                        dataKey={ key }
                                        label={ labels[ key ] } 
                                        onChange={ onDetailsChange } 
                                        type={ EXTRA_DATA[ data.type ][ key ] } 
                                        value={ data.details[ key ] } />
                                )
                            )
                        }
                        {
                            options.length > 0 ?
                                <Button aria-label={ labels.addInformation } onClick={ openMenu } startIcon={ <AddCircleOutlineIcon /> }>
                                    { labels.addInformation }
                                </Button> :
                                null
                        }
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button aria-label={ labels.commit } onClick={ commit } variant="contained" startIcon={ <CheckCircleOutlineIcon /> }>
                        { labels.commit }
                    </Button>
                    <Button aria-label={ labels.cancel } variant="outlined" onClick={ cancel } startIcon={ <NotInterestedIcon /> }>
                        { labels.cancel }
                    </Button>
                </DialogActions>
            </Dialog>
            <Menu
                anchorEl={ anchorEl }
                onClose={ closeMenu }
                open={ optionsOpen }>
                    {
                        options.map(
                            ( key, index ) => {
                                return (
                                    <MenuItem key={ 'menuOption-' + index } onClick={ addData( key ) }>
                                        <ListItemText>
                                            { labels[ key ] }
                                        </ListItemText>
                                    </MenuItem>
                                )
                            }
                        )
                    }
            </Menu>
        </>
    )
}

export default ModifyNode;